import { useState, useEffect } from 'react';
import { useLookupCollection } from '../../hooks/useLookupCollection';
import { slugifyLookupValue } from '../../services/lookupValueId';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';
import { TagInput } from '../CuratedWatches/TagInput';
import { LookupMultiSelect } from '../CuratedWatches/LookupMultiSelect';
import { WatchTypePropertiesPicker } from './WatchTypePropertiesPicker';
import type { LookupValue } from '../../types';
import toast from 'react-hot-toast';
import {
  getLookupCollectionConfig,
  getLookupGroup,
} from '../../config/lookupCollectionsConfig';
import {
  getExclusions,
  setExclusions,
  suggestLookupDescriptions,
} from '../../services/firebase';

interface LookupValueEditorProps {
  collectionName: string;
  displayName: string;
  onBack: () => void;
}

interface EditingValue extends Partial<LookupValue> {
  isNew?: boolean;
}

export function LookupValueEditor({
  collectionName,
  displayName,
  onBack,
}: LookupValueEditorProps) {
  const {
    collection,
    values,
    properties,
    loading,
    error,
    addValue,
    updateValue,
    bulkUpdateValues,
    deleteValue,
    reorder,
    updateProperties,
  } = useLookupCollection(collectionName);

  const [editingValue, setEditingValue] = useState<EditingValue | null>(null);
  const [synonymsList, setSynonymsList] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [collectionDescription, setCollectionDescription] = useState('');
  const [editingProperties, setEditingProperties] = useState<string[]>([]);
  const [newPropertyTag, setNewPropertyTag] = useState('');
  const [exclusions, setExclusionsList] = useState<string[]>([]);
  const [newExclusion, setNewExclusion] = useState('');
  const [exclusionsSaving, setExclusionsSaving] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);

  // AI "Suggest descriptions" flow
  const [suggesting, setSuggesting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  // slug -> suggested description
  const [descSuggestions, setDescSuggestions] = useState<Record<string, string>>({});
  // slug -> suggested rnQualifier (directness collection only)
  const [qualifierSuggestions, setQualifierSuggestions] = useState<Record<string, string>>({});
  // slug -> suggested rnReverseLabel (acquisition-types collection only)
  const [reverseLabelSuggestions, setReverseLabelSuggestions] = useState<Record<string, string>>({});
  // slug -> accepted?
  const [descAccepted, setDescAccepted] = useState<Record<string, boolean>>({});

  // The rnQualifier field is meaningful only on acquisition-directness values
  // (the RN app renders it as a small italic line under acquisition entries).
  // Gate the editor input + AI qualifier backfill on this collection.
  const isDirectness = collectionName === 'lookup_acquisition_directness';

  // The rnReverseLabel field is meaningful only on acquisition-TYPE values (the
  // RN app renders it as the reverse-direction label of the relationship). Gate
  // its editor input + AI backfill on this collection.
  const isAcquisitionTypes = collectionName === 'lookup_acquisition_types';

  const jsonString = collection ? JSON.stringify(collection, null, 2) : '';

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Get config info for this collection
  const collectionConfig = getLookupCollectionConfig(collectionName);
  const collectionGroup = collectionConfig ? getLookupGroup(collectionConfig.groupId) : undefined;

  // Initialize description from Firebase properties or config
  useEffect(() => {
    if (properties?.description) {
      setCollectionDescription(properties.description);
    } else if (collectionConfig?.description) {
      setCollectionDescription(collectionConfig.description);
    }
  }, [properties?.description, collectionConfig?.description]);

  // Load Algolia exclusions for this collection
  useEffect(() => {
    let cancelled = false;
    getExclusions(collectionName)
      .then((values) => {
        if (!cancelled) setExclusionsList(values);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load exclusions');
      });
    return () => {
      cancelled = true;
    };
  }, [collectionName]);

  const persistExclusions = async (next: string[]) => {
    const prev = exclusions;
    setExclusionsList(next);
    setExclusionsSaving(true);
    try {
      await setExclusions(collectionName, next);
    } catch {
      setExclusionsList(prev);
      toast.error('Failed to save exclusions');
    } finally {
      setExclusionsSaving(false);
    }
  };

  const handleAddExclusion = () => {
    const trimmed = newExclusion.trim();
    if (!trimmed) return;
    if (exclusions.includes(trimmed)) {
      toast.error('Already excluded');
      return;
    }
    setNewExclusion('');
    void persistExclusions([...exclusions, trimmed]);
  };

  const handleRemoveExclusion = (value: string) => {
    void persistExclusions(exclusions.filter((v) => v !== value));
  };

  const handleSave = async () => {
    if (!editingValue) return;

    const parsedSynonyms = properties?.supportsSynonyms
      ? synonymsList.filter(Boolean)
      : undefined;

    const cleanedProperties = editingProperties.filter((p) => p.trim() !== '');

    setIsProcessing(true);
    try {
      if (editingValue.isNew) {
        const nextSortOrder = values.length > 0
          ? Math.max(...values.map((v) => v.sortOrder ?? 0)) + 1
          : 1;

        // The key is what the value's id is minted from, so it has to be a
        // slug — normalise here rather than trusting what was typed.
        const key = slugifyLookupValue(editingValue.value || '');
        if (!key) {
          toast.error('Value (internal key) needs at least one letter or number');
          return;
        }

        await addValue({
          value: key,
          displayName: editingValue.displayName || '',
          isActive: editingValue.isActive ?? true,
          sortOrder: nextSortOrder,
          synonyms: parsedSynonyms,
          description: editingValue.description,
          rnQualifier: isDirectness
            ? editingValue.rnQualifier?.trim() || undefined
            : undefined,
          rnReverseLabel: isAcquisitionTypes
            ? editingValue.rnReverseLabel?.trim() || undefined
            : undefined,
          excludeFromAI: editingValue.excludeFromAI || undefined,
          metricLabel: properties?.supportsMeasurement ? editingValue.metricLabel : undefined,
          imperialLabel: properties?.supportsMeasurement ? editingValue.imperialLabel : undefined,
          properties: cleanedProperties.length > 0 ? cleanedProperties : undefined,
        });
        toast.success('Value added successfully');
      } else if (editingValue.id) {
        await updateValue(editingValue.id, {
          value: editingValue.value,
          displayName: editingValue.displayName,
          isActive: editingValue.isActive,
          synonyms: parsedSynonyms,
          description: editingValue.description,
          rnQualifier: isDirectness
            ? editingValue.rnQualifier?.trim() || undefined
            : undefined,
          rnReverseLabel: isAcquisitionTypes
            ? editingValue.rnReverseLabel?.trim() || undefined
            : undefined,
          excludeFromAI: editingValue.excludeFromAI ?? false,
          metricLabel: properties?.supportsMeasurement ? editingValue.metricLabel : undefined,
          imperialLabel: properties?.supportsMeasurement ? editingValue.imperialLabel : undefined,
          properties: cleanedProperties.length > 0 ? cleanedProperties : undefined,
        });
        toast.success('Value updated successfully');
      }
      setEditingValue(null);
    } catch {
      toast.error('Failed to save value');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsProcessing(true);
    try {
      await deleteValue(id);
      toast.success('Value deleted successfully');
      setDeleteConfirm(null);
    } catch {
      toast.error('Failed to delete value');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSuggestDescriptions = async () => {
    setSuggesting(true);
    try {
      // Generate for ALL values; pre-check gates overwrites per-row below.
      const result = await suggestLookupDescriptions(collectionName, {
        collectionDisplayName: displayName,
        onlyEmpty: false,
      });
      const suggestions = result.suggestions || {};
      const slugs = Object.keys(suggestions);
      if (slugs.length === 0) {
        toast('No descriptions suggested');
        return;
      }
      // Directness collection also gets AI-suggested rnQualifier phrases; other
      // collections return none.
      setQualifierSuggestions(result.rnQualifiers || {});
      // Acquisition-types collection also gets AI-suggested rnReverseLabel
      // strings; other collections return none.
      setReverseLabelSuggestions(result.rnReverseLabels || {});
      // Pre-check only rows whose existing description is empty, so re-runs
      // don't accidentally overwrite hand-written descriptions.
      const accepted: Record<string, boolean> = {};
      for (const slug of slugs) {
        const existing = values.find((v) => v.value === slug)?.description;
        accepted[slug] = !(existing && existing.trim());
      }
      setDescSuggestions(suggestions);
      setDescAccepted(accepted);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Lookup description suggestion failed:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to suggest descriptions';
      toast.error(message);
    } finally {
      setSuggesting(false);
    }
  };

  const handleApplyDescriptions = async () => {
    const slugToValue = new Map(values.map((v) => [v.value, v]));
    const updates = Object.entries(descSuggestions)
      .filter(([slug]) => descAccepted[slug] && slugToValue.has(slug))
      .map(([slug, description]) => {
        const existing = slugToValue.get(slug)!;
        const patch: Partial<LookupValue> = { description };
        // Directness only: fill the AI-suggested rnQualifier, but never
        // overwrite a qualifier the admin already set (non-destructive default).
        const suggestedQualifier = qualifierSuggestions[slug];
        if (
          isDirectness &&
          suggestedQualifier &&
          !(existing.rnQualifier && existing.rnQualifier.trim())
        ) {
          patch.rnQualifier = suggestedQualifier;
        }
        // Acquisition-types only: fill the AI-suggested rnReverseLabel, but never
        // overwrite one the admin already set (non-destructive default).
        const suggestedReverseLabel = reverseLabelSuggestions[slug];
        if (
          isAcquisitionTypes &&
          suggestedReverseLabel &&
          !(existing.rnReverseLabel && existing.rnReverseLabel.trim())
        ) {
          patch.rnReverseLabel = suggestedReverseLabel;
        }
        return { id: existing.id, patch };
      });
    if (updates.length === 0) {
      setShowSuggestions(false);
      return;
    }
    setApplyingSuggestions(true);
    try {
      // Single Firestore write + one refresh, so the list re-renders once.
      await bulkUpdateValues(updates);
      toast.success(
        `Applied ${updates.length} description${updates.length === 1 ? '' : 's'}`,
      );
      setShowSuggestions(false);
    } catch {
      toast.error('Failed to apply descriptions');
    } finally {
      setApplyingSuggestions(false);
    }
  };

  const handleToggleActive = async (value: LookupValue) => {
    try {
      await updateValue(value.id, { isActive: !value.isActive });
      toast.success(
        value.isActive ? 'Value deactivated' : 'Value activated'
      );
    } catch {
      toast.error('Failed to update value');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...values];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    await reorder(newOrder.map((v) => v.id));
  };

  const handleMoveDown = async (index: number) => {
    if (index === values.length - 1) return;
    const newOrder = [...values];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    await reorder(newOrder.map((v) => v.id));
  };

  const handlePropertyToggle = async (
    property: 'isActive' | 'supportsSynonyms' | 'supportsMeasurement' | 'isMultiSelect' | 'supportsGrouping',
    currentValue: boolean
  ) => {
    setIsProcessing(true);
    try {
      const newValue = !currentValue;
      await updateProperties({ [property]: newValue });

      // If enabling supportsMeasurement, copy value to both labels for all values
      if (property === 'supportsMeasurement' && newValue) {
        for (const value of values) {
          if (!value.metricLabel && !value.imperialLabel) {
            await updateValue(value.id, {
              metricLabel: value.displayName || value.value,
              imperialLabel: value.displayName || value.value,
            });
          }
        }
      }

      toast.success(`${property} ${newValue ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update property');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">{displayName}</h2>
              {collectionConfig?.isMultiSelect && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                  Multi-Select
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {collectionGroup && (
                <span className="text-xs text-brand-600 bg-brand-50 px-2 py-0.5 rounded">
                  {collectionGroup.displayName}
                </span>
              )}
              <span className="text-sm text-gray-500">{values.length} values</span>
            </div>
            {collectionDescription && (
              <p className="text-sm text-gray-500 mt-1 max-w-xl">{collectionDescription}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowJsonModal(true)}
            disabled={!collection}
          >
            View JSON
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowPropertiesPanel(!showPropertiesPanel)}
          >
            {showPropertiesPanel ? 'Hide' : 'Show'} Settings
          </Button>
          <Button
            variant="secondary"
            onClick={handleSuggestDescriptions}
            loading={suggesting}
            disabled={!collection || values.length === 0}
            title="Use Claude to draft descriptions for values that don't have one"
          >
            ✨ Suggest descriptions with AI
          </Button>
          <Button
            onClick={() => {
              const newValue: EditingValue = {
                isNew: true,
                value: '',
                displayName: '',
                isActive: true,
              };
              // Pre-populate measurement labels if enabled
              if (properties?.supportsMeasurement) {
                newValue.metricLabel = '';
                newValue.imperialLabel = '';
              }
              setEditingValue(newValue);
              setSynonymsList([]);
              setEditingProperties([]);
              setNewPropertyTag('');
            }}
          >
            Add Value
          </Button>
        </div>
      </div>

      {/* Collection Properties Panel */}
      {showPropertiesPanel && properties && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 space-y-4">
          <h3 className="font-medium text-gray-900">Collection Settings</h3>

          {/* Collection Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Collection ID
              </label>
              <div className="text-sm font-mono text-gray-600 bg-gray-100 px-3 py-2 rounded">
                {collectionName}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group
              </label>
              <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded">
                {collectionGroup?.displayName || 'Uncategorized'}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={collectionDescription}
                onChange={(e) => setCollectionDescription(e.target.value)}
                placeholder="Description of this lookup collection"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={isProcessing || collectionDescription === (properties.description || collectionConfig?.description || '')}
                onClick={async () => {
                  setIsProcessing(true);
                  try {
                    await updateProperties({ description: collectionDescription });
                    toast.success('Description updated');
                  } catch {
                    toast.error('Failed to update description');
                  } finally {
                    setIsProcessing(false);
                  }
                }}
              >
                Save
              </Button>
            </div>
          </div>

          {/* Feature Toggles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Features
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={properties.isActive}
                  onChange={() => handlePropertyToggle('isActive', properties.isActive)}
                  disabled={isProcessing}
                  className="rounded text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={properties.supportsSynonyms}
                  onChange={() => handlePropertyToggle('supportsSynonyms', properties.supportsSynonyms)}
                  disabled={isProcessing}
                  className="rounded text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">Supports Synonyms</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={properties.supportsMeasurement}
                  onChange={() => handlePropertyToggle('supportsMeasurement', properties.supportsMeasurement)}
                  disabled={isProcessing}
                  className="rounded text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">Supports Measurement</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={properties.isMultiSelect}
                  onChange={() => handlePropertyToggle('isMultiSelect', properties.isMultiSelect)}
                  disabled={isProcessing}
                  className="rounded text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">Multi-Select</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={properties.supportsGrouping}
                  onChange={() => handlePropertyToggle('supportsGrouping', properties.supportsGrouping)}
                  disabled={isProcessing}
                  className="rounded text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">Supports Grouping</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Algolia Exclusions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 space-y-3">
        <div>
          <h3 className="font-medium text-gray-900">Algolia Exclusions</h3>
          <p className="text-xs text-gray-500 mt-1">
            Values matching these terms will be excluded when syncing to Algolia search
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {exclusions.length === 0 ? (
            <span className="text-sm text-gray-500 italic">No exclusions set</span>
          ) : (
            exclusions.map((value) => (
              <span
                key={value}
                className="inline-flex items-center gap-1 bg-white border border-gray-300 rounded-full pl-3 pr-1 py-1 text-sm text-gray-700"
              >
                {value}
                <button
                  type="button"
                  onClick={() => handleRemoveExclusion(value)}
                  disabled={exclusionsSaving}
                  className="w-5 h-5 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                  aria-label={`Remove ${value}`}
                >
                  &times;
                </button>
              </span>
            ))
          )}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={newExclusion}
            onChange={(e) => setNewExclusion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddExclusion();
              }
            }}
            placeholder="Word or phrase to exclude"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={exclusionsSaving || !newExclusion.trim()}
            onClick={handleAddExclusion}
          >
            Add
          </Button>
        </div>
      </div>

      {/* Values Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Order
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Value
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Display Name
                </th>
                {properties?.supportsSynonyms && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Synonyms
                  </th>
                )}
                {properties?.supportsMeasurement && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Metric
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Imperial
                    </th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Properties
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {values.map((value, index) => (
                <tr key={value.id} className={!value.isActive ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="text-sm text-gray-500 w-6 text-center">
                        {value.sortOrder ?? index + 1}
                      </span>
                      <button
                        onClick={() => handleMoveDown(index)}
                        disabled={index === values.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">
                    {value.value}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {value.displayName}
                  </td>
                  {properties?.supportsSynonyms && (
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {value.synonyms && value.synonyms.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {value.synonyms.map((s, si) => (
                            <span key={si} className="inline-block px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">{s}</span>
                          ))}
                        </div>
                      ) : '-'}
                    </td>
                  )}
                  {properties?.supportsMeasurement && (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {value.metricLabel || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {value.imperialLabel || '-'}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {value.properties && value.properties.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {value.properties.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggleActive(value)}>
                      <Badge variant={value.isActive ? 'success' : 'default'}>
                        {value.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingValue(value);
                          setSynonymsList(value.synonyms || []);
                          setEditingProperties(value.properties ? [...value.properties] : []);
                          setNewPropertyTag('');
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(value.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {values.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No values in this collection. Click "Add Value" to create one.
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingValue}
        onClose={() => setEditingValue(null)}
        title={editingValue?.isNew ? 'Add Value' : 'Edit Value'}
        size="md"
      >
        {editingValue && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value (internal key)
              </label>
              <input
                type="text"
                value={editingValue.value || ''}
                onChange={(e) =>
                  setEditingValue({ ...editingValue, value: e.target.value })
                }
                onBlur={(e) => {
                  // New values only: the key is minted into the value's id, so
                  // fold it to a slug as soon as the field is left. Existing
                  // values keep whatever key they were saved with.
                  if (!editingValue.isNew) return;
                  const slug = slugifyLookupValue(e.target.value);
                  if (slug !== e.target.value) {
                    setEditingValue({ ...editingValue, value: slug });
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="e.g., stainless_steel"
              />
              <p className="mt-1 text-xs text-gray-500">
                Lowercase letters, numbers, <code>_</code> and <code>-</code> only
                &mdash; anything else is folded to an underscore when saved.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={editingValue.displayName || ''}
                onChange={(e) =>
                  setEditingValue({
                    ...editingValue,
                    displayName: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="e.g., Stainless Steel"
              />
            </div>

            {/* Synonyms - only show if collection supports it */}
            {properties?.supportsSynonyms && (
              <TagInput
                label="Synonyms"
                values={synonymsList}
                onChange={setSynonymsList}
                placeholder="Type synonym and press Enter..."
              />
            )}

            {/* Measurement labels - only show if collection supports it */}
            {properties?.supportsMeasurement && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Metric Label
                  </label>
                  <input
                    type="text"
                    value={editingValue.metricLabel || ''}
                    onChange={(e) =>
                      setEditingValue({
                        ...editingValue,
                        metricLabel: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="e.g., 100m"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Imperial Label
                  </label>
                  <input
                    type="text"
                    value={editingValue.imperialLabel || ''}
                    onChange={(e) =>
                      setEditingValue({
                        ...editingValue,
                        imperialLabel: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="e.g., 330ft"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={editingValue.description || ''}
                onChange={(e) =>
                  setEditingValue({
                    ...editingValue,
                    description: e.target.value,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                rows={2}
                placeholder="Optional description or help text"
              />
            </div>

            {isDirectness && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RN Qualifier (optional)
                </label>
                <input
                  type="text"
                  value={editingValue.rnQualifier || ''}
                  onChange={(e) =>
                    setEditingValue({
                      ...editingValue,
                      rnQualifier: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="e.g., via parent group"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Small italic line shown under acquisition entries in the RN app
                  (e.g. "via parent group"). Leave blank for no qualifier — only
                  used by acquisition-directness values.
                </p>
              </div>
            )}

            {isAcquisitionTypes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RN Reverse Label (optional)
                </label>
                <input
                  type="text"
                  value={editingValue.rnReverseLabel || ''}
                  onChange={(e) =>
                    setEditingValue({
                      ...editingValue,
                      rnReverseLabel: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="e.g., Acquired by"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Label shown on the REVERSE side of the relationship in the RN
                  app (e.g. "Acquired by" for the "Acquired" type).
                </p>
              </div>
            )}

            {/* Properties */}
            {collectionName === 'lookup_watch_types' ? (
              // Watch-type flags gate what the editor, the app and the AI prompt
              // treat as present on that kind of watch. Picked from a fixed
              // vocabulary — a free-text typo used to switch a section off
              // silently. See config/watchTypeProperties.ts.
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Properties</label>
                <WatchTypePropertiesPicker values={editingProperties} onChange={setEditingProperties} />
              </div>
            ) : collectionName === 'lookup_dial_styles' ? (
              // Dial styles store their constituent dial-color slugs in
              // `properties` (e.g. Pepsi → blue, red), consumed by the watchlock
              // backfill. Pick them from lookup_dial_colors instead of free text.
              <div>
                <LookupMultiSelect
                  label="Dial colors (properties)"
                  values={editingProperties}
                  lookupCollection="lookup_dial_colors"
                  onChange={setEditingProperties}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Constituent dial colors this style implies (e.g. Pepsi → blue, red).
                  Leave empty for finishes (Sunburst, Gilt) that apply on top of any color.
                </p>
              </div>
            ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Properties
              </label>
              {editingProperties.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {editingProperties.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm bg-purple-50 text-purple-700 border border-purple-200"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() =>
                          setEditingProperties(editingProperties.filter((t) => t !== tag))
                        }
                        className="text-purple-400 hover:text-purple-600"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newPropertyTag}
                  onChange={(e) => setNewPropertyTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const tag = newPropertyTag.trim();
                      if (tag && !editingProperties.includes(tag)) {
                        setEditingProperties([...editingProperties, tag]);
                        setNewPropertyTag('');
                      }
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="e.g., showMovement"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!newPropertyTag.trim() || editingProperties.includes(newPropertyTag.trim())}
                  onClick={() => {
                    const tag = newPropertyTag.trim();
                    if (tag && !editingProperties.includes(tag)) {
                      setEditingProperties([...editingProperties, tag]);
                      setNewPropertyTag('');
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={editingValue.isActive ?? true}
                onChange={(e) =>
                  setEditingValue({
                    ...editingValue,
                    isActive: e.target.checked,
                  })
                }
                className="rounded text-brand-600 focus:ring-brand-500"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                Active (visible in app)
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="excludeFromAI"
                checked={editingValue.excludeFromAI ?? false}
                onChange={(e) =>
                  setEditingValue({
                    ...editingValue,
                    excludeFromAI: e.target.checked,
                  })
                }
                className="rounded text-brand-600 focus:ring-brand-500"
              />
              <label htmlFor="excludeFromAI" className="text-sm text-gray-700">
                Exclude from AI (omit from enrichment prompt suggestions)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => setEditingValue(null)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={isProcessing}
                disabled={
                  !editingValue.value?.trim() ||
                  !editingValue.displayName?.trim()
                }
              >
                {editingValue.isNew ? 'Add' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Value"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete this value? This action cannot be
            undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              loading={isProcessing}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* JSON View Modal */}
      <Modal
        isOpen={showJsonModal}
        onClose={() => setShowJsonModal(false)}
        title={`Raw JSON: ${collectionName}`}
        size="xl"
      >
        <div className="space-y-4">
          <div className="relative">
            <button
              type="button"
              onClick={handleCopyJson}
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 text-gray-700"
            >
              {jsonCopied ? 'Copied!' : 'Copy'}
            </button>
            <pre className="max-h-[60vh] overflow-auto bg-gray-50 border border-gray-200 rounded-lg p-4 pr-20 text-xs font-mono text-gray-800 whitespace-pre">
              {jsonString}
            </pre>
          </div>
          <div className="flex justify-end pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setShowJsonModal(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* AI Description Suggestions Modal */}
      <Modal
        isOpen={showSuggestions}
        onClose={() => setShowSuggestions(false)}
        title="AI Suggested Descriptions"
        size="xl"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              Review Claude's suggested descriptions. Check the ones you want to
              keep, then click <span className="font-medium">Apply Selected</span>.
            </p>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setDescAccepted(
                    Object.fromEntries(
                      Object.keys(descSuggestions).map((slug) => [slug, true]),
                    ),
                  )
                }
              >
                Accept All
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setDescAccepted(
                    Object.fromEntries(
                      Object.keys(descSuggestions).map((slug) => [slug, false]),
                    ),
                  )
                }
              >
                Reject All
              </Button>
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-12"></th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Value
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Current
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Suggested
                  </th>
                  {isDirectness && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Suggested qualifier
                    </th>
                  )}
                  {isAcquisitionTypes && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Suggested reverse label
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(descSuggestions).map(([slug, suggested]) => {
                  const value = values.find((v) => v.value === slug);
                  const label = value?.displayName || slug;
                  const current = value?.description || '';
                  const suggestedQualifier = qualifierSuggestions[slug];
                  const currentQualifier = value?.rnQualifier?.trim();
                  const suggestedReverseLabel = reverseLabelSuggestions[slug];
                  const currentReverseLabel = value?.rnReverseLabel?.trim();
                  return (
                    <tr key={slug}>
                      <td className="px-3 py-2 text-center align-top">
                        <input
                          type="checkbox"
                          checked={!!descAccepted[slug]}
                          onChange={(e) =>
                            setDescAccepted((prev) => ({
                              ...prev,
                              [slug]: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300 mt-1"
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 align-top whitespace-nowrap">
                        {label}
                      </td>
                      <td className="px-3 py-2 text-gray-500 max-w-xs align-top whitespace-pre-wrap break-words">
                        {current || (
                          <span className="italic text-gray-400">empty</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-900 max-w-md align-top whitespace-pre-wrap break-words">
                        {suggested}
                      </td>
                      {isDirectness && (
                        <td className="px-3 py-2 align-top whitespace-pre-wrap break-words italic">
                          {suggestedQualifier ? (
                            <span className="text-gray-900">
                              {suggestedQualifier}
                              {currentQualifier && (
                                <span className="not-italic text-xs text-amber-600 ml-1">
                                  (keeps "{currentQualifier}")
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-300 not-italic">—</span>
                          )}
                        </td>
                      )}
                      {isAcquisitionTypes && (
                        <td className="px-3 py-2 align-top whitespace-pre-wrap break-words italic">
                          {suggestedReverseLabel ? (
                            <span className="text-gray-900">
                              {suggestedReverseLabel}
                              {currentReverseLabel && (
                                <span className="not-italic text-xs text-amber-600 ml-1">
                                  (keeps "{currentReverseLabel}")
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-300 not-italic">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setShowSuggestions(false)}
              disabled={applyingSuggestions}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyDescriptions}
              loading={applyingSuggestions}
              disabled={
                applyingSuggestions ||
                !Object.values(descAccepted).some(Boolean)
              }
            >
              Apply Selected
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
